import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  getCompanyDetailsQuery,
  updateCompanyQuery
} from "../../Graphql/querries/companyQuery";
import {
  GetCompanyResponse,
  UpdateCompanyInput,
  UpdateCompanyResponse
} from "../../components/Profile/types/profileTypes";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { setCompanyData } from "../../store/slices/profileSlice";
import { API_RESPONSE_CODE } from "@kivunova/kivufrontendcommon";

import { RootState } from "../../store/store";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

/**
 * useGetCompany
 * Fetch company info
 */
export const useGetCompany = () => {
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const companyData = useSelector(
    (state: RootState) => state.profile.companyData
  );
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<GetCompanyResponse, Error>({
    mutationKey: ["company"],

    mutationFn: async () => {
      const companyId = companyData?.id;
      const userId = userDetails?.id;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching company details",
        buildLogContext("company", "get-company", userId, companyId, storeId)
      );

      try {
        return await fetchGraphQL(
          getCompanyDetailsQuery,
          { input: {} },
          setAlert
        );
      } catch (error) {
        captureAppException(
          "Failed to fetch company details",
          error,
          buildLogContext("company", "get-company", userId, companyId, storeId)
        );
        throw error;
      }
    }
  });
};

/**
 * useUpdateCompany
 * Update existing company information
 */
export const useUpdateCompany = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<UpdateCompanyResponse, Error, UpdateCompanyInput>({
    mutationKey: ["updateCompany"],

    mutationFn: async (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating company details",
        buildLogContext(
          "company",
          "update-company",
          // userId not available in this hook — companyId comes from input
          undefined,
          input.companyId,
          // storeId not relevant for a company-level update
          undefined
        )
      );

      return fetchGraphQL(updateCompanyQuery, { input }, setAlert);
    },

    onSuccess: (response, input) => {
      const updateResult = response?.updateCompany;

      // ❌ Unexpected response shape
      if (!updateResult) {
        captureAppException(
          "Update company failed: missing updateCompany",
          new Error("Update company failed: missing updateCompany"),
          buildLogContext(
            "company",
            "update-company",
            undefined, // userId not available in onSuccess scope
            input.companyId,
            undefined // storeId not relevant for company-level update
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      // ⚠️ Expected API/business failure
      if (updateResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Company update rejected by API",
          buildLogContext(
            "company",
            "update-company",
            undefined, // userId not available in onSuccess scope
            input.companyId,
            undefined, // storeId not relevant for company-level update
            { apiCode: String(updateResult.code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      const updatedCompany = updateResult.company;

      // ❌ Logically broken success response
      if (!updatedCompany) {
        captureAppException(
          "API returned OK but company data missing",
          new Error("API returned OK but company data missing"),
          buildLogContext(
            "company",
            "update-company",
            undefined, // userId not available in onSuccess scope
            input.companyId,
            undefined // storeId not relevant for company-level update
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      // ✅ Success
      dispatch(setCompanyData(updatedCompany));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Company updated successfully",
        buildLogContext(
          "company",
          "update-company",
          undefined, // userId not available in onSuccess scope
          input.companyId,
          undefined // storeId not relevant for company-level update
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.COMPANY_UPDATED_SUCCESSFULLY
      });
    },

    onError: (error, input) => {
      captureAppException(
        "Failed to update company",
        error,
        buildLogContext(
          "company",
          "update-company",
          undefined, // userId not available in onError scope
          input?.companyId,
          undefined // storeId not relevant for company-level update
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useGetCompanyDetails
 * Fetch and store company details
 */
export const useGetCompanyDetails = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<GetCompanyResponse, Error>({
    mutationKey: ["company-details"],

    mutationFn: async () => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching company details",
        buildLogContext(
          "company",
          "get-company-details",
          // userId/companyId/storeId not yet available before the response
          undefined,
          undefined,
          undefined
        )
      );
      return fetchGraphQL(getCompanyDetailsQuery, { input: {} }, setAlert);
    },

    onSuccess: (response) => {
      const { company, code } = response.getCompanyDetails;

      if (!company || code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Failed to fetch company details",
          buildLogContext(
            "company",
            "get-company-details",
            undefined, // userId not available — response did not contain a valid company
            undefined, // companyId not available — response did not contain a valid company
            undefined, // storeId not relevant for company-level fetch
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as unknown as ALERT_BANNER_CODE
        });
        return;
      }

      dispatch(setCompanyData(company));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Company details loaded successfully",
        buildLogContext(
          "company",
          "get-company-details",
          undefined, // userId not stored on the company object
          company.id,
          undefined // storeId not relevant for company-level fetch
        )
      );
    }
  });
};
